<script setup lang="ts">
import { ChevronDown } from "lucide-vue-next";
import type { PublicWorkerEducation } from "~/types/public-worker";
import { toTitleCase } from "~/utils/search-catalog";

const props = defineProps<{
  items: PublicWorkerEducation[];
}>();

const INITIAL_ITEMS_COUNT = 3;

const { locale, t } = useI18n();
const showAll = ref(false);
const detailsOpen = ref(false);
const selectedItem = ref<PublicWorkerEducation | null>(null);

const sortedItems = computed(() => [...props.items].sort(compareByRecency));

const visibleItems = computed(() =>
  showAll.value
    ? sortedItems.value
    : sortedItems.value.slice(0, INITIAL_ITEMS_COUNT),
);

const canShowAll = computed(
  () => sortedItems.value.length > INITIAL_ITEMS_COUNT && !showAll.value,
);

function subtitle(item: PublicWorkerEducation) {
  const degreeKey = item.degree ? `education.${item.degree}` : "";
  const translated = degreeKey ? t(degreeKey) : "";
  const degree =
    degreeKey && translated !== degreeKey
      ? translated
      : item.degree
        ? toTitleCase(item.degree)
        : "";

  return [degree, item.fieldOfStudy].filter(Boolean).join(" · ");
}

function period(item: PublicWorkerEducation) {
  if (!item.startDate && !item.endDate) {
    return "";
  }

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(locale.value, {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(value));

  const start = item.startDate ? formatDate(item.startDate) : "";

  const end = item.endDate
    ? formatDate(item.endDate)
    : t("worker_profile.present");

  return start ? `${start} – ${end}` : String(end);
}

function compareByRecency(
  left: PublicWorkerEducation,
  right: PublicWorkerEducation,
) {
  const leftIsCurrent = Boolean(left.startDate && !left.endDate);
  const rightIsCurrent = Boolean(right.startDate && !right.endDate);

  if (leftIsCurrent !== rightIsCurrent) {
    return leftIsCurrent ? -1 : 1;
  }

  return dateTimestamp(right) - dateTimestamp(left);
}

function dateTimestamp(item: PublicWorkerEducation) {
  const value = item.endDate || item.startDate;
  const timestamp = value ? new Date(value).getTime() : 0;

  return Number.isFinite(timestamp) ? timestamp : 0;
}

function openDetails(item: PublicWorkerEducation) {
  if (!item.description) return;

  selectedItem.value = item;
  detailsOpen.value = true;
}
</script>

<template>
  <section
    v-if="sortedItems.length"
    id="education"
    class="scroll-mt-36 border-b border-app-border py-10 sm:py-12"
  >
    <WorkerPublicSectionHeading
      :title="$t('worker_profile.education')"
      :description="$t('worker_profile.education_description')"
    />

    <div class="mt-7 max-w-[840px]">
      <div>
        <WorkerPublicTimelineItem
          v-for="(item, index) in visibleItems"
          :key="item.id"
          :title="item.institutionName"
          :subtitle="subtitle(item)"
          :description="item.description"
          :period="period(item)"
          :last="index === visibleItems.length - 1"
          @details="openDetails(item)"
        />
      </div>

      <div v-if="canShowAll" class="mt-5 flex justify-center">
        <button
          type="button"
          class="inline-flex min-h-10 items-center gap-2 rounded-xl border border-app-border bg-white px-4 py-2 text-sm font-semibold text-app-text transition hover:border-primary/30 hover:bg-primary-50 hover:text-primary"
          @click="showAll = true"
        >
          {{
            $t("worker_profile.show_all_education", {
              count: sortedItems.length,
            })
          }}
          <ChevronDown class="size-4" />
        </button>
      </div>
    </div>

    <WorkerPublicBackgroundDetailsModal
      v-if="selectedItem"
      v-model="detailsOpen"
      type="education"
      :title="selectedItem.institutionName"
      :subtitle="subtitle(selectedItem)"
      :period="period(selectedItem)"
      :description="selectedItem.description || ''"
    />
  </section>
</template>
